import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useParams } from 'react-router-dom'
import { useSlugNavigate } from '../hooks/useNavigation'
import DashboardLayout from '../components/DashboardLayout'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:6200'

export default function WorkspaceManagementPage() {
  const { user, token, refreshUser } = useAuth()
  const navigate = useSlugNavigate()
  const { workspaceId } = useParams()
  const [workspace, setWorkspace] = useState(null)
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteType, setInviteType] = useState('user') // 'user', 'service_provider_manage', 'client_org'
  const [invitationLink, setInvitationLink] = useState('')
  const [inviteLoading, setInviteLoading] = useState(false)
  const [copiedLink, setCopiedLink] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    tenancy_type: 'SERVICE_PROVIDER',
    description: ''
  })
  const successTimeoutRef = useRef(null)

  // Check if there are unsaved changes
  const hasUnsavedChanges = workspace && (
    formData.name !== workspace.name ||
    formData.tenancy_type !== workspace.tenancy_type ||
    formData.description !== (workspace.description || '')
  )

  // Fetch workspace details and members
  useEffect(() => {
    const fetchWorkspaceData = async () => {
      try {
        // Fetch workspace details
        const workspaceResponse = await fetch(`${API_URL}/api/v1/tenancies/${workspaceId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (!workspaceResponse.ok) {
          throw new Error('Failed to fetch workspace details')
        }

        const workspaceData = await workspaceResponse.json()
        setWorkspace(workspaceData)
        setFormData({
          name: workspaceData.name,
          tenancy_type: workspaceData.tenancy_type,
          description: workspaceData.description || ''
        })

        // Fetch workspace members
        const membersResponse = await fetch(`${API_URL}/api/v1/tenancies/${workspaceId}/members`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (!membersResponse.ok) {
          throw new Error('Failed to fetch workspace members')
        }

        const membersData = await membersResponse.json()
        setMembers(membersData.members || [])
      } catch (err) {
        setError(err.message || 'Failed to load workspace data')
      } finally {
        setLoading(false)
      }
    }

    if (workspaceId && token) {
      fetchWorkspaceData()
    }
  }, [workspaceId, token])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')
    setSuccess(false)

    try {
      const response = await fetch(`${API_URL}/api/v1/tenancies/${workspaceId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to update workspace')
      }

      setWorkspace(data)
      setSuccess(true)

      // Refresh user data to update workspace information throughout the app
      await refreshUser()

      // Clear success message after 2 seconds
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current)
      }
      successTimeoutRef.current = setTimeout(() => setSuccess(false), 2000)
    } catch (err) {
      setError(err.message || 'Failed to update workspace')
    } finally {
      setSaving(false)
    }
  }

  const handleRemoveMember = async (memberId, memberRole) => {
    // Prevent removing owner
    if (memberRole === 'OWNER') {
      setError('Cannot remove the workspace owner')
      setTimeout(() => setError(''), 3000)
      return
    }

    if (!confirm('Are you sure you want to remove this member?')) {
      return
    }

    try {
      const response = await fetch(`${API_URL}/api/v1/tenancies/${workspaceId}/members/${memberId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.detail || 'Failed to remove member')
      }

      // Remove member from local state
      setMembers(prev => prev.filter(m => m.id !== memberId))
      setSuccess(true)
      setTimeout(() => setSuccess(false), 2000)
    } catch (err) {
      setError(err.message || 'Failed to remove member')
      setTimeout(() => setError(''), 3000)
    }
  }

  const handleUpdateMemberRole = async (memberId, newRole, currentRole) => {
    // Prevent changing owner role
    if (currentRole === 'OWNER') {
      setError('Cannot change the owner role')
      setTimeout(() => setError(''), 3000)
      return
    }

    try {
      const response = await fetch(`${API_URL}/api/v1/tenancies/${workspaceId}/members/${memberId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ role: newRole })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to update member role')
      }

      // Update member in local state
      setMembers(prev => prev.map(m =>
        m.id === memberId ? { ...m, role: newRole } : m
      ))
      setSuccess(true)
      setTimeout(() => setSuccess(false), 2000)
    } catch (err) {
      setError(err.message || 'Failed to update member role')
      setTimeout(() => setError(''), 3000)
    }
  }

  const handleCreateInvitation = async () => {
    setInviteLoading(true)
    setError('')
    setInvitationLink('')
    setCopiedLink(false)

    try {
      const response = await fetch(`${API_URL}/api/v1/invitations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          tenancy_id: workspaceId,
          invitation_type: inviteType,
          role: inviteType === 'user' ? 'MEMBER' : 'VIEWER'
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create invitation')
      }

      // Generate the invitation link
      const link = `${window.location.origin}/invite/${data.token}`
      setInvitationLink(link)
    } catch (err) {
      setError(err.message || 'Failed to create invitation link')
    } finally {
      setInviteLoading(false)
    }
  }

  const handleCopyLink = () => {
    navigator.clipboard.writeText(invitationLink)
    setCopiedLink(true)
    setTimeout(() => setCopiedLink(false), 2000)
  }

  const handleCloseInviteModal = () => {
    setShowInviteModal(false)
    setInvitationLink('')
    setError('')
    setCopiedLink(false)
    setInviteType('user')
  }

  const getRoleBadgeColor = (role) => {
    const colors = {
      OWNER: 'bg-purple-100 text-purple-700',
      ADMIN: 'bg-blue-100 text-blue-700',
      MEMBER: 'bg-green-100 text-green-700',
      VIEWER: 'bg-gray-100 text-gray-700'
    }
    return colors[role] || colors.VIEWER
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-[#617589] text-sm">Loading workspace...</p>
        </div>
      </div>
    )
  }

  return (
    <DashboardLayout variant="settings">
      {/* Top Navigation Bar */}
      <header className="flex items-center justify-between whitespace-nowrap border-b border-solid border-[#dbe0e6] bg-white px-8 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-4 text-[#111418]">
          <button
            onClick={() => navigate('/settings/organizations')}
            className="flex items-center gap-2 text-[#617589] hover:text-primary transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">arrow_back</span>
            <span className="text-sm font-medium">Back to Organizations</span>
          </button>
        </div>
      </header>

      {/* Page Content */}
      <div className="p-8 max-w-6xl mx-auto w-full">
        {/* Page Heading */}
        <div className="mb-8">
          <h1 className="text-[#111418] text-3xl font-black leading-tight tracking-[-0.033em] mb-2">
            Manage Organization
          </h1>
          <p className="text-[#617589] text-base font-normal leading-normal">
            Edit organization details and manage team members
          </p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 rounded-lg bg-green-50 p-4 border border-green-200">
            <div className="flex items-center">
              <span className="material-symbols-outlined text-green-600 mr-3 text-[20px]">check_circle</span>
              <p className="text-sm font-medium text-green-800">Changes saved successfully</p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 rounded-lg bg-red-50 p-4 border border-red-200">
            <div className="flex items-start">
              <span className="material-symbols-outlined text-red-600 mr-3 text-[20px]">error</span>
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Organization Details Card */}
        <div className="bg-white rounded-xl border border-[#dbe0e6] shadow-sm mb-8">
          <div className="px-6 py-5 border-b border-[#dbe0e6]">
            <h2 className="text-[#111418] text-lg font-bold">Organization Details</h2>
          </div>

          <div className="p-6 space-y-6">
            {/* Organization Name */}
            <label className="flex flex-col">
              <p className="text-[#111418] text-sm font-semibold leading-normal pb-2">
                Organization Name <span className="text-red-500">*</span>
              </p>
              <input
                className="form-input flex w-full resize-none overflow-hidden rounded-lg text-[#111418] border border-[#dbe0e6] bg-white h-12 px-4 placeholder:text-[#617589] text-base font-normal leading-normal focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow"
                placeholder="e.g., Acme Corporation"
                required
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
            </label>

            {/* Organization Type */}
            <div className="flex flex-col">
              <p className="text-[#111418] text-sm font-semibold leading-normal pb-2">
                Organization Type <span className="text-red-500">*</span>
              </p>
              <p className="text-[#617589] text-xs mb-3">This setting mainly affects your dashboard view</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Service Provider Option */}
                <button
                  type="button"
                  onClick={() => handleChange({ target: { name: 'tenancy_type', value: 'SERVICE_PROVIDER' } })}
                  className={`flex flex-col p-4 rounded-lg border-2 transition-all text-left ${
                    formData.tenancy_type === 'SERVICE_PROVIDER'
                      ? 'border-primary bg-primary/5'
                      : 'border-[#dbe0e6] hover:border-[#b0b8c4] bg-white'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      formData.tenancy_type === 'SERVICE_PROVIDER' ? 'bg-primary/20 text-primary' : 'bg-gray-100 text-[#617589]'
                    }`}>
                      <span className="material-symbols-outlined text-[22px]">business_center</span>
                    </div>
                    <div className="flex-1">
                      <p className={`font-semibold ${formData.tenancy_type === 'SERVICE_PROVIDER' ? 'text-primary' : 'text-[#111418]'}`}>
                        Service Provider
                      </p>
                    </div>
                    {formData.tenancy_type === 'SERVICE_PROVIDER' && (
                      <span className="material-symbols-outlined text-primary text-[20px]">check_circle</span>
                    )}
                  </div>
                  <p className="text-[#617589] text-sm">
                    You provide services to other organizations (e.g., accounting firm, consultancy)
                  </p>
                </button>

                {/* Client Option */}
                <button
                  type="button"
                  onClick={() => handleChange({ target: { name: 'tenancy_type', value: 'CLIENT' } })}
                  className={`flex flex-col p-4 rounded-lg border-2 transition-all text-left ${
                    formData.tenancy_type === 'CLIENT'
                      ? 'border-primary bg-primary/5'
                      : 'border-[#dbe0e6] hover:border-[#b0b8c4] bg-white'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      formData.tenancy_type === 'CLIENT' ? 'bg-primary/20 text-primary' : 'bg-gray-100 text-[#617589]'
                    }`}>
                      <span className="material-symbols-outlined text-[22px]">apartment</span>
                    </div>
                    <div className="flex-1">
                      <p className={`font-semibold ${formData.tenancy_type === 'CLIENT' ? 'text-primary' : 'text-[#111418]'}`}>
                        Client
                      </p>
                    </div>
                    {formData.tenancy_type === 'CLIENT' && (
                      <span className="material-symbols-outlined text-primary text-[20px]">check_circle</span>
                    )}
                  </div>
                  <p className="text-[#617589] text-sm">
                    Your organization receives services from providers (e.g., a business with an accountant)
                  </p>
                </button>
              </div>
            </div>

            {/* Description */}
            <label className="flex flex-col">
              <p className="text-[#111418] text-sm font-semibold leading-normal pb-2">
                Description <span className="text-[#617589] text-xs font-normal">(Optional)</span>
              </p>
              <textarea
                className="form-input flex w-full resize-none overflow-hidden rounded-lg text-[#111418] border border-[#dbe0e6] bg-white p-4 placeholder:text-[#617589] text-base font-normal leading-normal focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow"
                placeholder="Brief description of this organization"
                rows="3"
                name="description"
                value={formData.description}
                onChange={handleChange}
              />
            </label>

            {/* Unsaved Changes Warning */}
            {hasUnsavedChanges && (
              <div className="rounded-lg bg-amber-50 p-4 border border-amber-200">
                <div className="flex items-center">
                  <span className="material-symbols-outlined text-amber-600 mr-3 text-[20px]">warning</span>
                  <p className="text-sm font-medium text-amber-800">You have unsaved changes</p>
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="flex justify-end pt-4">
              <button
                onClick={handleSave}
                disabled={saving || !hasUnsavedChanges}
                className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>

        {/* Members Management Card */}
        <div className="bg-white rounded-xl border border-[#dbe0e6] shadow-sm">
          <div className="px-6 py-5 border-b border-[#dbe0e6] flex items-center justify-between">
            <h2 className="text-[#111418] text-lg font-bold">Team Members</h2>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setInviteType('user')
                  setShowInviteModal(true)
                }}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-semibold text-sm"
              >
                <span className="material-symbols-outlined text-[18px]">person_add</span>
                Invite Member
              </button>
              {workspace?.tenancy_type === 'SERVICE_PROVIDER' && (
                <button
                  onClick={() => {
                    setInviteType('service_provider_manage')
                    setShowInviteModal(true)
                  }}
                  className="flex items-center gap-2 px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary/5 transition-colors font-semibold text-sm"
                >
                  <span className="material-symbols-outlined text-[18px]">business</span>
                  Invite Service Provider
                </button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#f8fafc]">
                  <th className="px-6 py-3 text-[#617589] text-xs font-bold uppercase tracking-wider">Member</th>
                  <th className="px-6 py-3 text-[#617589] text-xs font-bold uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-[#617589] text-xs font-bold uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-[#617589] text-xs font-bold uppercase tracking-wider">Joined</th>
                  <th className="px-6 py-3 text-[#617589] text-xs font-bold uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#dbe0e6]">
                {members.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-primary/20 rounded-full h-10 w-10 flex items-center justify-center text-primary font-bold text-sm">
                          {member.first_name?.[0]?.toUpperCase() || member.email?.[0]?.toUpperCase() || 'U'}
                        </div>
                        <span className="text-[#111418] font-semibold text-sm">
                          {member.first_name && member.last_name
                            ? `${member.first_name} ${member.last_name}`
                            : member.email?.split('@')[0] || 'User'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#617589]">{member.email}</td>
                    <td className="px-6 py-4">
                      {member.role === 'OWNER' ? (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${getRoleBadgeColor(member.role)}`}>
                          {member.role}
                        </span>
                      ) : (
                        <select
                          value={member.role}
                          onChange={(e) => handleUpdateMemberRole(member.id, e.target.value, member.role)}
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border-0 cursor-pointer ${getRoleBadgeColor(member.role)}`}
                        >
                          <option value="ADMIN">ADMIN</option>
                          <option value="MEMBER">MEMBER</option>
                          <option value="VIEWER">VIEWER</option>
                        </select>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-[#617589]">
                      {new Date(member.joined_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {member.role !== 'OWNER' && (
                        <button
                          onClick={() => handleRemoveMember(member.id, member.role)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium transition-colors"
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {members.length === 0 && (
              <div className="p-8 text-center">
                <p className="text-[#617589] text-sm">No members found</p>
              </div>
            )}
          </div>
        </div>

        {/* Managed Organizations (Only for Service Providers) */}
        {workspace?.tenancy_type === 'SERVICE_PROVIDER' && (
          <div className="bg-white rounded-xl border border-[#dbe0e6] shadow-sm mt-8">
            <div className="px-6 py-5 border-b border-[#dbe0e6] flex items-center justify-between">
              <div>
                <h2 className="text-[#111418] text-lg font-bold">Managed Organizations</h2>
                <p className="text-[#617589] text-sm mt-1">Client organizations managed by this service provider</p>
              </div>
              <button
                onClick={() => {
                  setInviteType('client_org')
                  setShowInviteModal(true)
                }}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-semibold text-sm"
              >
                <span className="material-symbols-outlined text-[18px]">groups</span>
                Invite Client
              </button>
            </div>
            <div className="p-6">
              <p className="text-[#617589] text-sm text-center py-8">
                No managed organizations yet. Use the "Invite Client" button above to connect with client organizations.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Invitation Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[#111418] text-2xl font-bold">
                {inviteType === 'user' && 'Invite Team Member'}
                {inviteType === 'service_provider_manage' && 'Invite Service Provider'}
                {inviteType === 'client_org' && 'Invite Client Organization'}
              </h2>
              <button
                onClick={handleCloseInviteModal}
                className="text-[#617589] hover:text-[#111418] transition-colors"
              >
                <span className="material-symbols-outlined text-[24px]">close</span>
              </button>
            </div>

            {!invitationLink ? (
              <>
                <p className="text-[#617589] mb-6">
                  {inviteType === 'user' && 'Create an invitation link to invite a team member to join this organization.'}
                  {inviteType === 'service_provider_manage' && 'Create an invitation link for a service provider to manage this organization.'}
                  {inviteType === 'client_org' && 'Create an invitation link for a client organization to be managed by you.'}
                </p>

                {error && (
                  <div className="rounded-lg bg-red-50 p-4 border border-red-200 mb-4">
                    <div className="flex items-start">
                      <span className="material-symbols-outlined text-red-600 mr-3 text-[20px]">error</span>
                      <p className="text-sm font-medium text-red-800">{error}</p>
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={handleCloseInviteModal}
                    className="flex-1 px-4 py-3 border border-[#dbe0e6] text-[#111418] rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateInvitation}
                    disabled={inviteLoading}
                    className="flex-1 px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-semibold disabled:opacity-50"
                  >
                    {inviteLoading ? 'Creating...' : 'Generate Link'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <span className="material-symbols-outlined text-green-600 text-[24px]">check_circle</span>
                    <div>
                      <p className="text-green-800 font-semibold mb-1">Invitation Link Created!</p>
                      <p className="text-green-700 text-sm">
                        Share this link to invite {inviteType === 'user' ? 'the team member' : inviteType === 'service_provider_manage' ? 'the service provider' : 'the client organization'}.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-[#111418] text-sm font-semibold mb-2">
                    Invitation Link
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={invitationLink}
                      readOnly
                      className="flex-1 px-4 py-3 border border-[#dbe0e6] rounded-lg bg-[#f0f2f4] text-[#111418] text-sm font-mono"
                    />
                    <button
                      onClick={handleCopyLink}
                      className="px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-semibold"
                    >
                      {copiedLink ? (
                        <span className="material-symbols-outlined text-[20px]">check</span>
                      ) : (
                        <span className="material-symbols-outlined text-[20px]">content_copy</span>
                      )}
                    </button>
                  </div>
                  {copiedLink && (
                    <p className="text-green-600 text-sm mt-2">Link copied to clipboard!</p>
                  )}
                </div>

                <button
                  onClick={handleCloseInviteModal}
                  className="w-full px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-semibold"
                >
                  Done
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  )
}
